/**
 * Tempo e fusi orari.
 *
 * Regola architetturale (ARCHITECTURE.md §C9): tutta la matematica del planner
 * avviene in *local wall time* della destinazione. Questo modulo e' l'UNICO posto
 * dove si converte fra istante assoluto (Date/UTC) e orario locale.
 *
 * Nessuna dipendenza esterna: si usa Intl, disponibile sia in Node sia nel browser.
 */

export interface ZonedParts {
  readonly year: number;
  readonly month: number; // 1-12
  readonly day: number; // 1-31
  readonly hour: number; // 0-23
  readonly minute: number; // 0-59
  readonly second: number; // 0-59
}

/** 'YYYY-MM-DD' nel fuso indicato. */
export type LocalDate = string;
/** 'HH:MM' 24h. */
export type LocalTime = string;

export function isValidTimeZone(timeZone: string): boolean {
  if (timeZone === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = partsFormatterCache.get(timeZone);
  if (cached !== undefined) return cached;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  partsFormatterCache.set(timeZone, formatter);
  return formatter;
}

/** Scompone un istante assoluto nell'orario da orologio del fuso indicato. */
export function toZonedParts(instant: Date, timeZone: string): ZonedParts {
  const parts = partsFormatter(timeZone).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((p) => p.type === type);
    if (found === undefined) throw new Error(`parte ${type} mancante per il fuso ${timeZone}`);
    return Number(found.value);
  };
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

function partsToUtcMillis(parts: ZonedParts): number {
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}

/** Offset del fuso (in minuti) valido per l'istante indicato. */
export function timeZoneOffsetMinutes(instant: Date, timeZone: string): number {
  const asUtc = partsToUtcMillis(toZonedParts(instant, timeZone));
  return (asUtc - instant.getTime()) / 60_000;
}

/**
 * Inverso di toZonedParts: da orario locale a istante assoluto.
 *
 * Due passaggi perche' l'offset dipende dall'istante stesso (DST). Il secondo
 * passaggio corregge il salto; e' l'algoritmo standard e converge sempre, tranne
 * nell'ora inesistente del passaggio all'ora legale, dove restituisce l'istante
 * immediatamente successivo al salto (comportamento documentato e testato).
 */
export function zonedPartsToInstant(parts: ZonedParts, timeZone: string): Date {
  const naive = partsToUtcMillis(parts);
  const firstGuess = new Date(naive - timeZoneOffsetMinutes(new Date(naive), timeZone) * 60_000);
  const secondOffset = timeZoneOffsetMinutes(firstGuess, timeZone);
  return new Date(naive - secondOffset * 60_000);
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** 'YYYY-MM-DD' locale della destinazione. */
export function toLocalDate(instant: Date, timeZone: string): LocalDate {
  const p = toZonedParts(instant, timeZone);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** 'HH:MM' locale della destinazione. */
export function toLocalTime(instant: Date, timeZone: string): LocalTime {
  const p = toZonedParts(instant, timeZone);
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

export function isValidLocalDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

export function isValidLocalTime(value: string): boolean {
  if (!/^\d{2}:\d{2}$/.test(value)) return false;
  const [h, m] = value.split(':').map(Number) as [number, number];
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

export function localTimeToMinutes(value: LocalTime): number {
  if (!isValidLocalTime(value)) throw new RangeError(`orario non valido: ${value}`);
  const [h, m] = value.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

export function minutesToLocalTime(minutes: number): LocalTime {
  const normalized = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(normalized / 60))}:${pad2(normalized % 60)}`;
}

/** Combina data locale + ora locale nel fuso indicato e restituisce l'istante. */
export function localDateTimeToInstant(
  date: LocalDate,
  time: LocalTime,
  timeZone: string,
): Date {
  if (!isValidLocalDate(date)) throw new RangeError(`data non valida: ${date}`);
  if (!isValidLocalTime(time)) throw new RangeError(`orario non valido: ${time}`);
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const [hour, minute] = time.split(':').map(Number) as [number, number];
  return zonedPartsToInstant({ year, month, day, hour, minute, second: 0 }, timeZone);
}

export function addLocalDays(date: LocalDate, days: number): LocalDate {
  if (!isValidLocalDate(date)) throw new RangeError(`data non valida: ${date}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
}

export function localDateDiffInDays(from: LocalDate, to: LocalDate): number {
  if (!isValidLocalDate(from)) throw new RangeError(`data non valida: ${from}`);
  if (!isValidLocalDate(to)) throw new RangeError(`data non valida: ${to}`);
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/**
 * Giornate del viaggio: ogni data locale (nel fuso della destinazione) toccata
 * dall'intervallo arrivo-partenza, estremi inclusi.
 *
 * Il giorno di arrivo conta anche se si arriva alle 23:40, e il giorno di partenza
 * conta anche se si riparte alle 06:00: sono comunque giornate da organizzare.
 */
export function enumerateTripDays(
  startsAt: Date,
  endsAt: Date,
  timeZone: string,
): LocalDate[] {
  if (!isValidTimeZone(timeZone)) throw new RangeError(`fuso orario non valido: ${timeZone}`);
  if (endsAt.getTime() < startsAt.getTime()) {
    throw new RangeError("la partenza non puo' precedere l'arrivo");
  }
  const first = toLocalDate(startsAt, timeZone);
  const last = toLocalDate(endsAt, timeZone);
  const count = localDateDiffInDays(first, last);
  return Array.from({ length: count + 1 }, (_unused, index) => addLocalDays(first, index));
}

/** Giorno della settimana 0=domenica ... 6=sabato, per la data locale indicata. */
export function localDayOfWeek(date: LocalDate): number {
  if (!isValidLocalDate(date)) throw new RangeError(`data non valida: ${date}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/**
 * "Quando devo partire?" (brief §32).
 *
 *   recommended_departure = target - route_duration - safety_buffer
 *
 * Funzione pura, indipendente dal provider di routing e dalla UI.
 */
export function recommendedDeparture(params: {
  targetTime: Date;
  routeDurationSec: number;
  safetyBufferMin: number;
}): Date {
  if (params.routeDurationSec < 0) throw new RangeError('durata percorso negativa');
  if (params.safetyBufferMin < 0) throw new RangeError('buffer negativo');
  return new Date(
    params.targetTime.getTime() -
      params.routeDurationSec * 1000 -
      params.safetyBufferMin * 60_000,
  );
}
